<template>
  <table class="table table-striped table-bordered table-condensed hypervisor-list-table">
    <thead>
      <tr>
        <th class="hypervisor-col hypervisor-col--name"><div>Name</div></th>
        <th class="hypervisor-col hypervisor-col--status"><div class="text-center">Status</div></th>
        <th class="hypervisor-col hypervisor-col--cpu hidden-tablet"><div class="text-center">CPU(MHz)[Core]</div></th>
        <th class="hypervisor-col hypervisor-col--memory"><div class="text-center">Memory Usage</div></th>
        <th class="hypervisor-col hypervisor-col--cpu-usage"><div class="text-center">CPU Usage</div></th>
        <th class="hypervisor-col hypervisor-col--ip hidden-tablet"><div class="text-center">IP address</div></th>
        <th class="hypervisor-col hypervisor-col--vms hypervisor-col--vms-responsive"><div class="text-center">VMs</div></th>
        <th v-if="showActions" class="hypervisor-col hypervisor-col--actions"><div class="text-center">Actions</div></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="`hv-${row.id}`">
        <td>
          <div class="hypervisor-name-cell">
            <div class="hypervisor-name-cell__name">{{ row.name }}</div>
            <div v-if="row.description" class="hypervisor-name-cell__description hidden-tablet">{{ row.description }}</div>
          </div>
        </td>
        <td class="text-center">
          <span class="app-status-chip" :class="row.status_class">{{ row.status_label }}</span>
        </td>
        <td class="hidden-tablet hypervisor-cell--cpu">{{ row.cpu_label }}</td>
        <td>
          <div class="progress">
            <div class="progress-bar" :class="progressClass(row.mem_usage)" roll="progressbar" aria-valuemin="0" aria-valuemax="100" :style="{ width: `${row.mem_usage}%` }">
              <span style="display:none;">{{ row.mem_usage }}%</span>
            </div>
          </div>
        </td>
        <td>
          <div class="progress">
            <div class="progress-bar" :class="progressClass(row.cpu_usage)" roll="progress" aria-valuemin="0" aria-valuemax="100" :style="{ width: `${row.cpu_usage}%` }">
              <span style="display:none;">{{ row.cpu_usage }}%</span>
            </div>
          </div>
        </td>
        <td class="hidden-tablet">{{ row.ipaddress }}</td>
        <td class="hypervisor-cell--vms-responsive">{{ row.vm_count_label }}</td>
        <td v-if="showActions" class="hypervisor-actions-col">
          <div class="hypervisor-more-actions" v-if="row.actions">
            <button
              type="button"
              class="hypervisor-more-actions__gear"
              :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
              @click.stop="toggleMenu(row.id, $event)"
            >
              <AppIcon name="settings" />
              <AppIcon name="chevron-down" class="hypervisor-more-actions__caret" />
            </button>
            <div
              v-if="openMenuId === row.id"
              class="hypervisor-more-actions__menu"
              :style="menuStyle"
              @click.stop
            >
              <a :href="row.actions.edit_url" class="hypervisor-more-actions__item">
                <AppIcon name="edit" />
                <span>Edit</span>
              </a>
              <form
                :action="row.actions.destroy_url"
                method="post"
                class="hypervisor-more-actions__form"
                :data-confirm-message="row.actions.destroy_confirm_message"
                @click.stop
              >
                <input v-if="csrfToken" type="hidden" name="authenticity_token" :value="csrfToken">
                <input type="hidden" name="_method" value="delete">
                <button type="submit" class="hypervisor-more-actions__item hypervisor-more-actions__item--danger" title="Delete HyperVisor">
                  <AppIcon name="delete" />
                  <span>Delete</span>
                </button>
              </form>
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [] })
  }
})

const rowsState = ref(props.initialData?.rows || [])
const rows = computed(() => rowsState.value)
const showActions = computed(() => rows.value.some((row) => row.actions))
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const openMenuId = ref(null)
const menuStyle = ref({})

watch(
  () => props.initialData,
  (nextValue) => {
    rowsState.value = nextValue?.rows || []
  }
)

function progressClass(value) {
  if (value <= 60) return 'progress-bar-info'
  if (value <= 80) return 'progress-bar-warning'
  return 'progress-bar-danger'
}

function handleHyperVisorUpdate(event) {
  const payload = event.detail || {}

  if (payload.type === 'removed') {
    rowsState.value = rowsState.value.filter((row) => row.id !== payload.hyper_visor_id)
    return
  }

  if (payload.type !== 'changed' || !payload.row_data) {
    return
  }

  const nextRow = payload.row_data
  const index = rowsState.value.findIndex((row) => row.id === nextRow.id)
  if (index === -1) return

  const nextRows = rowsState.value.slice()
  nextRows.splice(index, 1, { ...rowsState.value[index], ...nextRow })
  rowsState.value = nextRows
}

function toggleMenu(rowId, event) {
  if (openMenuId.value === rowId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 180
  const menuHeight = 110
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) left = viewportWidth - menuWidth - 12
  if (left < 12) left = 12
  if (top + menuHeight > viewportHeight - 12) top = rect.top - menuHeight - gap
  if (top < 12) top = 12

  openMenuId.value = rowId
  menuStyle.value = { position: 'fixed', top: `${top}px`, left: `${left}px` }
}

function closeMenu() {
  openMenuId.value = null
  menuStyle.value = {}
}

function handleDocumentClick(event) {
  if (!event.target.closest('.hypervisor-more-actions__menu') && !event.target.closest('.hypervisor-more-actions__gear')) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('scc:hyper-visor-state-changed', handleHyperVisorUpdate)
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('scc:hyper-visor-state-changed', handleHyperVisorUpdate)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
.hypervisor-list-table {
  table-layout: fixed;
}

.hypervisor-col--status {
  width: 146px;
}

.hypervisor-list-table :deep(.app-status-chip) {
  min-width: 126px;
  padding-inline: 14px;
}

.hypervisor-col--cpu {
  width: 172px;
}

.hypervisor-col--memory,
.hypervisor-col--cpu-usage {
  width: 118px;
}

.hypervisor-col--ip {
  width: 146px;
}

.hypervisor-col--vms {
  width: 156px;
}

.hypervisor-name-cell {
  text-align: left;
}

.hypervisor-name-cell__name {
  font-weight: 600;
  color: #0f172a;
}

.hypervisor-name-cell__description {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}

.hypervisor-col--actions {
  width: 74px;
}

.hypervisor-more-actions {
  position: relative;
}

.hypervisor-more-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.hypervisor-more-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.hypervisor-more-actions__menu {
  z-index: 60;
  min-width: 180px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.hypervisor-more-actions__form {
  display: block;
}

.hypervisor-more-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.hypervisor-more-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.hypervisor-more-actions__item--danger {
  color: #9f1d2d;
}

.hypervisor-more-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}

@media (max-width: 1350px) {
  .hypervisor-col--cpu,
  .hypervisor-cell--cpu {
    display: none;
  }
}

@media (max-width: 1150px) {
  .hypervisor-col--vms-responsive,
  .hypervisor-cell--vms-responsive {
    display: none;
  }
}
</style>
